import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";

import {
  type AcceptCarrierInviteResponse,
  useAcceptCarrierInvite,
} from "@/api/carrier";
import { PortalState } from "@/components/portal-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PENDING_INVITE_KEY = "ezx_pending_invite_token";

function getNameError(value: string): string | null {
  const name = value.trim();
  if (!name) return "Enter your name.";
  if (name.length > 120) return "Name must be 120 characters or fewer.";
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name)) {
    return "Enter your name, not an email address.";
  }
  return null;
}

function getAcceptError(error: unknown): {
  title: string;
  message: string;
  retryable: boolean;
  switchAccount?: boolean;
} {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  const serverError = isAxiosError<{ error?: unknown }>(error)
    ? error.response?.data?.error
    : undefined;
  if (status === 400) {
    return {
      title: "Check your invitation details",
      message:
        typeof serverError === "string" && serverError.trim()
          ? serverError
          : "Check your name and try accepting the invitation again.",
      retryable: false,
    };
  }
  if (status === 404) {
    return {
      title: "Invalid invitation",
      message:
        "This invitation link is not valid. Contact the person who invited you to resend it.",
      retryable: false,
    };
  }
  if (status === 409) {
    if (
      serverError ===
        "Auth0 account is already linked to another carrier user" ||
      serverError === "Email is already linked to a different account"
    ) {
      return {
        title: "Sign in with the invited account",
        message:
          "This invitation cannot be linked to your current account. Sign in with the email that received the invitation to continue. If you are already using that email, contact your inviter.",
        retryable: false,
        switchAccount: true,
      };
    }
    if (serverError !== "Invite already accepted") {
      return {
        title: "Invite cannot be accepted",
        message:
          "This invitation conflicts with an existing carrier or operator account. Contact your inviter or carrier administrator for help.",
        retryable: false,
      };
    }
    return {
      title: "Invite already accepted",
      message:
        "This invitation has already been accepted. Sign in with the account that joined the team, or contact your inviter.",
      retryable: false,
    };
  }
  if (status === 410) {
    return {
      title: "Invite expired",
      message:
        "This invite has expired. Contact the person who invited you to send a new invitation.",
      retryable: false,
    };
  }
  return {
    title: "Invite could not be accepted",
    message:
      "We could not accept this invite because of a temporary problem. Try again. If it keeps happening, contact your inviter.",
    retryable: true,
  };
}

/**
 * Public landing for invite recipients. Flow:
 *
 *   1. User clicks email link → /accept-invite/:token
 *   2. We persist :token to localStorage and trigger Auth0 signup (the
 *      Auth0 redirect roundtrip wipes the URL, so we MUST persist before
 *      kicking off the redirect)
 *   3. Auth0 returns to /callback → SessionProvider populates the session
 *   4. Auth0 then navigates back to /accept-invite/:token (we re-mount with
 *      a live session) — we read auth0.user.sub + name and POST to the
 *      public accept endpoint
 *   5. On success, clear the pending-invite key. Owners/dispatchers redirect
 *      to / (the portal); an OPERATOR has no portal, so we render a "get PAL"
 *      success state pointing at the driver app instead.
 *
 * Errors (expired / invalid / already-accepted) surface inline with a
 * "contact your inviter" message.
 */
export default function AcceptInviteScreen() {
  const { token: pathToken } = useParams<{ token: string }>();
  const location = useLocation();
  const queryToken = new URLSearchParams(location.search).get("token");
  const token = pathToken ?? queryToken ?? undefined;
  const navigate = useNavigate();
  const auth0 = useAuth0();
  const auth0Configured = Boolean(
    import.meta.env.VITE_AUTH0_DOMAIN && import.meta.env.VITE_AUTH0_CLIENT_ID
  );
  const accept = useAcceptCarrierInvite();
  const acceptanceInFlight = useRef(false);
  const [started, setStarted] = useState(false);
  const profileName = auth0.user?.name ?? "";
  const needsName = getNameError(profileName) !== null;
  const [enteredName, setEnteredName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const name = (enteredName ?? (needsName ? "" : profileName)).trim();
  const validationFailure =
    accept.isError &&
    isAxiosError(accept.error) &&
    accept.error.response?.status === 400;
  const nameFeedback =
    nameError ??
    (validationFailure ? getAcceptError(accept.error).message : null);
  const [signupState, setSignupState] = useState<"idle" | "pending" | "error">(
    "idle"
  );
  // Set only for OPERATOR accepts: an operator has no portal, so instead of
  // redirecting to `/` (a dead end) we render a "get PAL" success state.
  const [operatorAccept, setOperatorAccept] =
    useState<AcceptCarrierInviteResponse | null>(null);

  const submitAcceptance = useCallback(() => {
    if (
      acceptanceInFlight.current ||
      !token ||
      !auth0.isAuthenticated ||
      !auth0.user?.sub ||
      getNameError(name)
    ) {
      return;
    }
    acceptanceInFlight.current = true;
    setStarted(true);
    void accept
      .mutateAsync({
        token,
        name,
        auth0Id: auth0.user.sub,
      })
      .then((result) => {
        try {
          window.localStorage.removeItem(PENDING_INVITE_KEY);
        } catch {
          // no-op
        }
        // Operators have no portal — land them on "get PAL", not `/`.
        if (result.role === "OPERATOR" || result.palInstallUrl) {
          setOperatorAccept(result);
          return;
        }
        void navigate("/");
      })
      .catch(() => {
        acceptanceInFlight.current = false;
        // surfaced via accept.isError below
      });
  }, [accept, auth0, name, navigate, token]);

  // Once Auth0 is configured AND the user is signed in AND we have a token,
  // fire the accept call exactly once.
  useEffect(() => {
    if (started || accept.isError || needsName) return;
    submitAcceptance();
  }, [accept.isError, needsName, started, submitAcceptance]);

  /**
   * Send the invitee to the identity provider to establish an identity, then
   * return here so the accept call can run with the resulting auth0Id.
   *
   * `mode` matters: Auth0 refuses to sign up an email that already has an
   * account (400 from /u/signup/password), and Universal Login renders that as
   * an opaque "Something went wrong, please try again later". An invitee who
   * already belongs to ANOTHER carrier is exactly that case — one CarrierUser
   * can hold many memberships, so a second invite is normal and must not
   * dead-end on a signup attempt. Hence "log in" is a first-class action here,
   * not something to discover after a failure.
   */
  const startAuth0 = async (mode: "signup" | "login" | "switch") => {
    if (!token) return;
    setSignupState("pending");
    try {
      window.localStorage.setItem(PENDING_INVITE_KEY, token);
    } catch {
      // best-effort; Auth0 returnTo will still bring us back to this URL
    }
    try {
      if (!auth0Configured || !auth0.loginWithRedirect) {
        throw new Error("Sign-in provider is not configured");
      }
      await auth0.loginWithRedirect({
        appState: { returnTo: `/accept-invite/${encodeURIComponent(token)}` },
        // Omitting screen_hint lands on the login screen instead of signup.
        ...(mode === "signup"
          ? { authorizationParams: { screen_hint: "signup" } }
          : mode === "switch"
            ? { authorizationParams: { prompt: "login" } }
            : {}),
      });
    } catch {
      setSignupState("error");
    }
  };

  const handleAcceptRetry = () => {
    accept.reset();
    submitAcceptance();
  };

  if (!token) {
    return (
      <PortalState
        title="Invalid invite link"
        description={
          <p>
            The invite link is missing a token. Contact the person who invited
            you to resend.
          </p>
        }
      />
    );
  }

  if ((auth0Configured && auth0.isLoading) || accept.isPending) {
    return (
      <PortalState
        title={accept.isPending ? "Accepting invite..." : "Loading invite..."}
        loading
        description={null}
      />
    );
  }

  if (accept.isError && !validationFailure) {
    const error = getAcceptError(accept.error);
    return (
      <PortalState
        title={error.title}
        description={
          <>
            <p role="alert">{error.message}</p>
            {error.switchAccount && auth0.user?.email && (
              <p className="mt-2 break-words">
                Signed in as {auth0.user.email}.
              </p>
            )}
          </>
        }
      >
        {error.switchAccount && (
          <div className="flex flex-col items-center gap-3">
            <Button
              size="touch"
              disabled={signupState === "pending"}
              onClick={() => void startAuth0("switch")}
            >
              {signupState === "pending" && (
                <Loader2 className="animate-spin" />
              )}
              Sign in with invited email
            </Button>
            {signupState === "error" && (
              <p role="alert" className="text-sm leading-5 text-destructive">
                We could not open the sign-in page. Please try again.
              </p>
            )}
          </div>
        )}
        {error.retryable && (
          <Button size="touch" onClick={handleAcceptRetry}>
            Try again
          </Button>
        )}
      </PortalState>
    );
  }

  if (operatorAccept) {
    return (
      <PortalState
        title="You're in — get the PAL app"
        description={<p>Install the PAL driver app to start delivering.</p>}
      >
        {operatorAccept.palInstallUrl && (
          <Button asChild size="touch">
            <a
              href={operatorAccept.palInstallUrl}
              target="_blank"
              rel="noreferrer"
            >
              Get PAL
            </a>
          </Button>
        )}
      </PortalState>
    );
  }

  if (auth0.isAuthenticated && !auth0.user?.sub) {
    return (
      <PortalState
        title="Invite cannot be accepted"
        description={
          <p>
            We could not identify your account after sign in. Contact the person
            who invited you to resend the invitation.
          </p>
        }
      />
    );
  }

  if (auth0.isAuthenticated) {
    if ((needsName && !started) || validationFailure) {
      return (
        <PortalState
          title="What's your name?"
          description={
            <p>Enter your name to finish accepting your invitation.</p>
          }
        >
          <form
            className="flex w-full flex-col gap-4 text-left"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              const error = getNameError(name);
              setNameError(error);
              if (error) return;
              accept.reset();
              submitAcceptance();
            }}
          >
            <div className="space-y-2">
              <label htmlFor="invite-name" className="text-sm font-medium">
                Your name
              </label>
              <Input
                id="invite-name"
                autoComplete="name"
                required
                value={enteredName ?? (needsName ? "" : profileName)}
                onChange={(event) => {
                  setEnteredName(event.target.value);
                  setNameError(null);
                }}
                aria-invalid={Boolean(nameFeedback)}
                aria-describedby={
                  nameFeedback ? "invite-name-error" : undefined
                }
              />
              {nameFeedback && (
                <p
                  id="invite-name-error"
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {nameFeedback}
                </p>
              )}
            </div>
            <Button type="submit" size="touch">
              Accept invitation
            </Button>
          </form>
        </PortalState>
      );
    }
    return <PortalState title="Accepting invite..." loading />;
  }

  return (
    <PortalState
      title="You've been invited"
      description={
        <p>
          Join the carrier portal and you&apos;ll be added to your team
          automatically. Already use this email for another carrier? Log in
          instead — the invite is added to your existing account.
        </p>
      }
    >
      <div className="flex flex-col items-center gap-3">
        <Button
          size="touch"
          disabled={signupState === "pending"}
          onClick={() => void startAuth0("signup")}
        >
          {signupState === "pending" && <Loader2 className="animate-spin" />}
          {signupState === "pending" ? "Opening sign up..." : "Sign up"}
        </Button>
        {/* Copy never names the identity provider: it is an implementation
            detail to the invitee, and it is swappable. */}
        <Button
          size="touch"
          variant="outline"
          disabled={signupState === "pending"}
          onClick={() => void startAuth0("login")}
        >
          Already have an account? Log in
        </Button>
        {signupState === "error" && (
          <p role="alert" className="text-sm leading-5 text-destructive">
            We could not open the sign-in page. Please try again.
          </p>
        )}
      </div>
    </PortalState>
  );
}
